import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useChatToasts } from "@/lib/use-chat-toasts";
import { cn } from "@/lib/utils";
import { Bell, CheckCheck, ExternalLink } from "lucide-react";

interface Notification {
    id: string;
    type: string;
    title: string;
    body: string | null;
    link: string | null;
    is_read: boolean;
    created_at: string;
}

interface NotificationsResponse {
    notifications: Notification[];
    total: number;
    unreadCount: number;
}

function timeAgo(dateStr: string): string {
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

/**
 * `triggerClassName` lets a host that owns its own chrome supply the button
 * styling — the office toolbar (top-bar-v3) uses its `icobtn` class so the bell
 * sits at the same 38px as the theme toggle beside it, rather than importing a
 * Tailwind-shaped button into a hand-rolled toolbar. Default is the field app's
 * look, so existing call sites are unchanged.
 */
export function NotificationBell({ triggerClassName }: { triggerClassName?: string } = {}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { isOffice } = useAuth();

    const { data } = useQuery<NotificationsResponse>({
        queryKey: ["notifications"],
        queryFn: () => api.get("/api/notifications?limit=5"),
        refetchInterval: 60_000,
    });

    const markReadMut = useMutation({
        mutationFn: (id: string) => api.patch(`/api/notifications/${id}/read`),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
    });

    const markAllMut = useMutation({
        mutationFn: () => api.post("/api/notifications/read-all"),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
    });

    const handleClickOutside = useCallback((e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) {
            setOpen(false);
        }
    }, []);

    useEffect(() => {
        if (open) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open, handleClickOutside]);

    // Live chat toasts (office users) — the shared hook, so they survive the
    // bell being unmounted by the quiet toolbar (lib/use-chat-toasts.ts).
    useChatToasts(isOffice);

    const unread = data?.unreadCount ?? 0;
    const notifications = data?.notifications ?? [];

    function handleNotificationClick(n: Notification) {
        if (!n.is_read) markReadMut.mutate(n.id);
        if (n.link) {
            navigate(n.link);
            setOpen(false);
        }
    }

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen(!open)}
                className={cn(
                    "relative",
                    triggerClassName ?? "rounded-md p-2 hover:bg-muted text-foreground/80 transition-colors",
                )}
                title="Notifications"
                aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications — nothing new"}
            >
                <Bell className="h-5 w-5" />
                {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                        {unread > 99 ? "99+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 top-full mt-2 w-80 max-h-[28rem] overflow-hidden rounded-xl border bg-card shadow-lg z-50 flex flex-col">
                    <div className="flex items-center justify-between px-4 py-3 border-b">
                        <h3 className="text-sm font-semibold">Notifications</h3>
                        {unread > 0 && (
                            <button
                                onClick={() => markAllMut.mutate()}
                                disabled={markAllMut.isPending}
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50"
                            >
                                <CheckCheck className="h-3.5 w-3.5" />
                                Mark all read
                            </button>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto divide-y">
                        {notifications.length === 0 ? (
                            <div className="py-10 text-center text-sm text-foreground/80">No notifications</div>
                        ) : (
                            notifications.map((n) => (
                                <button
                                    key={n.id}
                                    onClick={() => handleNotificationClick(n)}
                                    className={cn(
                                        "w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors flex gap-3",
                                        !n.is_read && "bg-primary/5",
                                    )}
                                >
                                    <div className="pt-0.5">
                                        {!n.is_read && <span className="block h-2 w-2 rounded-full bg-primary" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p
                                            className={cn(
                                                "text-sm truncate",
                                                !n.is_read ? "font-medium" : "text-foreground/80",
                                            )}
                                        >
                                            {n.title}
                                        </p>
                                        {n.body && (
                                            <p className="text-xs text-foreground/80 mt-0.5 line-clamp-2 whitespace-pre-line">
                                                {n.body}
                                            </p>
                                        )}
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-[11px] text-foreground/80">
                                                {timeAgo(n.created_at)}
                                            </span>
                                            {n.link && <ExternalLink className="h-3 w-3 text-foreground/80" />}
                                        </div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>

                    {/* The dropdown is a peek, not a workspace (#996). Reading
                        and clearing happens on the page, where nothing is
                        truncated and history is reachable. */}
                    <button
                        onClick={() => {
                            setOpen(false);
                            navigate("/notifications");
                        }}
                        className="border-t px-4 py-2.5 text-sm text-primary hover:bg-muted/50 transition-colors text-left font-medium"
                    >
                        See all notifications →
                    </button>
                </div>
            )}
        </div>
    );
}
